/**
 * Contact form — submits to HubSpot Forms API (v3 integration submit).
 *
 * Endpoint: https://api.hsforms.com/submissions/v3/integration/submit/{portalId}/{formId}
 *
 * Field names on the form match the HubSpot internal property names from the
 * client's mapping doc. We collect them via FormData, transform to the API's
 * expected shape, and POST. Success and error states are shown in-page.
 *
 * Notes:
 * - The newsletter opt-in maps to legalConsentOptions.consent.communications.
 *   The subscriptionTypeId is HubSpot-portal-specific; until the client confirms
 *   the actual subscription ID for "Marketing Information" on Portal 50831863,
 *   we send the opt-in as a regular field instead so it still lands in HubSpot.
 * - The Forms API v3 integration endpoint does NOT require authentication —
 *   it's a public endpoint keyed on portalId+formId. Safe to call from the
 *   storefront with no secret.
 */
(function () {
  'use strict';

  var FORM_ID_ATTR = 'data-hs-form-id';
  var PORTAL_ID_ATTR = 'data-hs-portal-id';

  // Reads a cookie by name — used for HubSpot's tracking cookie `hubspotutk`.
  function getCookie(name) {
    var match = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return match ? decodeURIComponent(match[1]) : '';
  }

  // Fields whose values should not be sent to HubSpot as named properties.
  // The newsletter checkbox is a UI-only flag we read separately.
  var NON_HUBSPOT_FIELDS = new Set(['newsletter_optin']);

  function getValue(formEl, name) {
    var el = formEl.querySelector('[name="' + name + '"]');
    if (!el) return '';
    if (el.type === 'checkbox') return el.checked ? el.value : '';
    if (el.type === 'radio') {
      var checked = formEl.querySelector('[name="' + name + '"]:checked');
      return checked ? checked.value : '';
    }
    return el.value || '';
  }

  function buildFields(formEl) {
    // Collect each named control once. Skip empty values — HubSpot rejects
    // empty strings for some property types (e.g. dropdowns).
    var seen = new Set();
    var fields = [];
    var controls = formEl.querySelectorAll('input[name], select[name], textarea[name]');
    controls.forEach(function (el) {
      var name = el.name;
      if (!name || seen.has(name) || NON_HUBSPOT_FIELDS.has(name)) return;
      seen.add(name);

      var value = getValue(formEl, name);
      if (!value) return;

      fields.push({ objectTypeId: '0-1', name: name, value: value });
    });
    return fields;
  }

  function buildPayload(formEl) {
    var fields = buildFields(formEl);
    var newsletterEl = formEl.querySelector('#contact-newsletter-optin');
    var optedIn = !!(newsletterEl && newsletterEl.checked);

    var context = {
      pageUri: window.location.href,
      pageName: document.title,
    };
    // Links this submission to the visitor's HubSpot session/source (traffic source, page views)
    // when the tracking cookie is present. Only included if set, so submissions still work without it.
    var hutk = getCookie('hubspotutk');
    if (hutk) context.hutk = hutk;

    var payload = {
      fields: fields,
      context: context,
    };

    // Marketing consent — recorded but the actual subscriptionTypeId for
    // "Marketing Information" on Portal 50831863 is TBD. Sending as a custom
    // field for now; can swap to legalConsentOptions once HubSpot confirms the ID.
    if (optedIn) {
      payload.fields.push({ objectTypeId: '0-1', name: 'email_opt_in', value: 'true' });
    }

    return payload;
  }

  function showSuccess(formEl) {
    var success = formEl.querySelector('#contact-form-success');
    var errors = formEl.querySelector('#contact-form-errors');
    if (errors) errors.hidden = true;
    if (success) {
      success.hidden = false;
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    formEl.reset();
  }

  function showError(formEl, message) {
    var errors = formEl.querySelector('#contact-form-errors');
    if (!errors) return;
    var p = errors.querySelector('p');
    if (p) p.textContent = message || 'Something went wrong submitting your message. Please try again.';
    errors.hidden = false;
    errors.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function setSubmitting(formEl, isSubmitting) {
    var btn = formEl.querySelector('.contact-form__submit');
    if (!btn) return;
    if (isSubmitting) {
      btn.dataset.originalLabel = btn.textContent;
      btn.textContent = 'SENDING…';
      btn.disabled = true;
    } else {
      if (btn.dataset.originalLabel) btn.textContent = btn.dataset.originalLabel;
      btn.disabled = false;
    }
  }

  async function submitToHubSpot(formEl) {
    var portalId = formEl.getAttribute(PORTAL_ID_ATTR);
    var formId = formEl.getAttribute(FORM_ID_ATTR);
    if (!portalId || !formId) {
      throw new Error('HubSpot form is missing portalId or formId attributes.');
    }
    var url = 'https://api.hsforms.com/submissions/v3/integration/submit/' + portalId + '/' + formId;
    var res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(formEl)),
    });
    if (!res.ok) {
      var detail = '';
      try {
        var data = await res.json();
        detail = (data.errors && data.errors.length ? data.errors.map(function (e) { return e.message; }).join('; ') : data.message) || '';
      } catch (e) {}
      throw new Error('HubSpot submission failed (' + res.status + ')' + (detail ? ': ' + detail : ''));
    }
    return res.json();
  }

  function init() {
    var formEl = document.getElementById('contact-form-hubspot');
    if (!formEl) return;

    formEl.addEventListener('submit', async function (e) {
      e.preventDefault();

      // Native HTML5 validation first — short-circuit if anything required is missing.
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      setSubmitting(formEl, true);
      try {
        await submitToHubSpot(formEl);
        showSuccess(formEl);
      } catch (err) {
        showError(formEl, err && err.message);
        if (window && window.console) console.error('[contact-form] submission error:', err);
      } finally {
        setSubmitting(formEl, false);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
